import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';

const COR_PADRAO  = [30, 64, 175];
const COR_CINZA   = [107, 114, 128];
const COR_VERDE   = [22, 163, 74];
const COR_VERMELHO = [220, 38, 38];

function hexParaRgb(hex) {
  if (!hex || !/^#[0-9a-fA-F]{6}$/.test(hex)) return COR_PADRAO;
  return [parseInt(hex.slice(1, 3), 16), parseInt(hex.slice(3, 5), 16), parseInt(hex.slice(5, 7), 16)];
}

function moeda(v) {
  return parseFloat(v || 0).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
}

function data(v) {
  if (!v) return '—';
  const s = typeof v === 'string' ? v : v instanceof Date ? v.toISOString() : String(v);
  const [y, m, d] = s.split('T')[0].split('-');
  if (!y || !m || !d) return '—';
  return `${d}/${m}/${y}`;
}

function labelStatus(s) {
  const m = {
    orcamento: 'Orçamento', em_andamento: 'Em Andamento',
    em_andamento_alerta: 'Em Andamento', atrasada: 'Atrasada',
    concluida: 'Obra Entregue', concluida_pgto_pendente: 'Entregue · Pgto. Pendente',
    finalizada: 'Obra Finalizada', cancelada: 'Cancelada',
    pausada: 'Pausada', orcamento_recusado: 'Orç. Recusado',
    pendente: 'Pendente', atrasado: 'Atrasado', concluido: 'Concluído',
  };
  return m[s] || s;
}

function nomeArquivo(prefixo, obra) {
  const nome = obra.nome_obra.replace(/[^a-zA-Z0-9À-ú ]/g, '').trim().replace(/ +/g, '-').toLowerCase();
  return `${prefixo}-obra-${obra.obraid}-${nome}.pdf`;
}

function cabecalho(doc, obra, config) {
  const cor = hexParaRgb(config?.cor);
  const statusAtual = obra.status_calculado || obra.status_obra;
  const pw = doc.internal.pageSize.getWidth();

  doc.setFillColor(...cor);
  doc.rect(0, 0, pw, 28, 'F');
  doc.setTextColor(255, 255, 255);

  const temLogo      = !!(config?.logo);
  const temEmpresa   = !!(config?.empresa_nome);
  const logoRatio    = parseFloat(config?.logo_ratio || 3);

  let xTexto = 14;

  if (temLogo) {
    // bounding box: max 44mm wide, max 18mm tall, centrado verticalmente na barra
    const MAX_W = 44, MAX_H = 18;
    let lw = MAX_W;
    let lh = lw / logoRatio;
    if (lh > MAX_H) { lh = MAX_H; lw = lh * logoRatio; }
    const ly = (28 - lh) / 2;
    try { doc.addImage(config.logo, 'PNG', 14, ly, lw, lh); } catch {}
    xTexto = 14 + lw + 4;
  }

  const maxTextW = pw - xTexto - 14;

  if (temEmpresa) {
    doc.setFontSize(10);
    doc.setFont('helvetica', 'bold');
    doc.text(config.empresa_nome.toUpperCase(), xTexto, 9);

    doc.setFontSize(7.5);
    doc.setFont('helvetica', 'normal');
    let linhaInfo = '';
    if (config?.cnpj) linhaInfo = `CNPJ: ${config.cnpj}`;
    if (linhaInfo) doc.text(linhaInfo, xTexto, 15);

    doc.setFontSize(9);
    doc.setFont('helvetica', 'bold');
    const nomeObra = doc.splitTextToSize(`Obra: ${obra.nome_obra}`, maxTextW);
    doc.text(nomeObra, xTexto, config?.cnpj ? 22 : 18);
  } else if (temLogo) {
    doc.setFontSize(12);
    doc.setFont('helvetica', 'bold');
    const nomeObra = doc.splitTextToSize(obra.nome_obra, maxTextW);
    doc.text(nomeObra, xTexto, 16);
  } else {
    doc.setFontSize(16);
    doc.setFont('helvetica', 'bold');
    doc.text('CronoObra', 14, 11);
    doc.setFontSize(8);
    doc.setFont('helvetica', 'normal');
    doc.text('by BRASZTECH', 14, 17);
    doc.setFontSize(13);
    doc.setFont('helvetica', 'bold');
    const nomeObra = doc.splitTextToSize(obra.nome_obra, 120);
    doc.text(nomeObra, 14, 26);
  }

  doc.setTextColor(...COR_CINZA);
  doc.setFontSize(8);
  doc.setFont('helvetica', 'normal');

  let y = 38;
  const linhas = [
    ['Cliente', obra.nome_cliente],
    ['Status', labelStatus(statusAtual)],
  ];
  if (obra.logradouro || obra.cidade) {
    const end = [obra.logradouro, obra.bairro].filter(Boolean).join(', ');
    const loc = [obra.cidade, obra.estado].filter(Boolean).join('/');
    linhas.push(['Endereço', [end, loc].filter(Boolean).join(', ')]);
  }

  doc.setTextColor(50, 50, 50);
  for (const [label, valor] of linhas) {
    doc.setFont('helvetica', 'bold');
    const textoLabel = `${label}:`;
    doc.text(textoLabel, 14, y);
    const larguraLabel = doc.getTextWidth(textoLabel);
    doc.setFont('helvetica', 'normal');
    doc.text(String(valor || '—'), 14 + larguraLabel + 2, y);
    y += 5;
  }

  return y + 4;
}

function secao(doc, titulo, y, cor) {
  const pw = doc.internal.pageSize.getWidth();
  doc.setFillColor(243, 244, 246);
  doc.rect(14, y, pw - 28, 7, 'F');
  doc.setTextColor(...(cor || COR_PADRAO));
  doc.setFontSize(9);
  doc.setFont('helvetica', 'bold');
  doc.text(titulo, 16, y + 5);
  return y + 10;
}

function rodape(doc, config) {
  const total = doc.internal.getNumberOfPages();
  const gerado = new Date().toLocaleString('pt-BR');
  const nomeRodape = 'CronoObra by BraszTech';
  for (let i = 1; i <= total; i++) {
    doc.setPage(i);
    const pw = doc.internal.pageSize.getWidth();
    const ph = doc.internal.pageSize.getHeight();
    doc.setFontSize(7);
    doc.setTextColor(...COR_CINZA);
    doc.setFont('helvetica', 'normal');
    doc.text(`Gerado em ${gerado} · ${nomeRodape}`, 14, ph - 7);
    doc.text(`Página ${i} de ${total}`, pw - 14, ph - 7, { align: 'right' });
  }
}

// ─── CRONOGRAMA ──────────────────────────────────────────────────────────────

export function exportarCronograma(obra, materiaisData, config = {}) {
  const cor = hexParaRgb(config?.cor);
  const doc = new jsPDF({ unit: 'mm', format: 'a4' });
  let y = cabecalho(doc, obra, config);

  // Serviços
  y = secao(doc, 'SERVIÇOS', y, cor);
  const servicos = obra.servicos || [];
  if (servicos.length === 0) {
    doc.setFontSize(8); doc.setTextColor(...COR_CINZA);
    doc.text('Nenhum serviço cadastrado.', 14, y); y += 8;
  } else {
    autoTable(doc, {
      startY: y,
      margin: { left: 14, right: 14 },
      headStyles: { fillColor: cor, textColor: 255, fontSize: 8 },
      bodyStyles: { fontSize: 8 },
      alternateRowStyles: { fillColor: [249, 250, 251] },
      head: [['#', 'Serviço', 'Descrição', 'Início', 'Fim', 'Status', 'Atraso']],
      body: servicos.map((s, i) => [
        i + 1,
        s.nome_servico,
        s.descricao_servico_obra || '—',
        data(s.data_inicio),
        data(s.data_fim),
        labelStatus(s.status_servico),
        s.dias_atraso > 0 ? `${s.dias_atraso} dia(s)` : '—',
      ]),
      columnStyles: { 0: { cellWidth: 8 }, 3: { cellWidth: 20 }, 4: { cellWidth: 20 }, 5: { cellWidth: 25 }, 6: { cellWidth: 22 } },
    });
    y = doc.lastAutoTable.finalY + 8;
  }

  // Materiais
  if (y > 240) { doc.addPage(); y = 20; }
  y = secao(doc, 'MATERIAIS', y, cor);
  const materiais = materiaisData?.materiais || [];
  if (materiais.length === 0) {
    doc.setFontSize(8); doc.setTextColor(...COR_CINZA);
    doc.text('Nenhum material cadastrado.', 14, y);
  } else {
    autoTable(doc, {
      startY: y,
      margin: { left: 14, right: 14 },
      headStyles: { fillColor: cor, textColor: 255, fontSize: 8 },
      bodyStyles: { fontSize: 8 },
      alternateRowStyles: { fillColor: [249, 250, 251] },
      head: [['#', 'Código', 'Descrição', 'Unidade', 'Quantidade']],
      body: materiais.map((m, i) => [
        i + 1,
        m.codigo_sinapi || '—',
        m.descricao,
        m.unidade || '—',
        parseFloat(m.quantidade).toLocaleString('pt-BR'),
      ]),
      columnStyles: { 0: { cellWidth: 8 }, 1: { cellWidth: 22 }, 3: { cellWidth: 20 }, 4: { cellWidth: 25 } },
    });
  }

  rodape(doc, config);
  doc.save(nomeArquivo('cronograma', obra));
}

// ─── RELATÓRIO COMPLETO ───────────────────────────────────────────────────────

export function exportarRelatorioCompleto(obra, materiaisData, pagamentoData, config = {}) {
  const cor = hexParaRgb(config?.cor);
  const doc = new jsPDF({ unit: 'mm', format: 'a4' });
  let y = cabecalho(doc, obra, config);

  const totalOrcado  = obra.total_orcado || 0;
  const totalMat     = materiaisData?.total_materiais || 0;
  const valorOrcado  = totalOrcado + totalMat;
  const valorFechado = parseFloat(obra.valor_fechado_obra || 0);

  // Serviços
  y = secao(doc, 'SERVIÇOS', y, cor);
  const servicos = obra.servicos || [];
  if (servicos.length === 0) {
    doc.setFontSize(8); doc.setTextColor(...COR_CINZA);
    doc.text('Nenhum serviço cadastrado.', 14, y); y += 8;
  } else {
    autoTable(doc, {
      startY: y,
      margin: { left: 14, right: 14 },
      headStyles: { fillColor: cor, textColor: 255, fontSize: 8 },
      bodyStyles: { fontSize: 8 },
      alternateRowStyles: { fillColor: [249, 250, 251] },
      head: [['#', 'Serviço', 'Descrição', 'Início', 'Fim', 'Status', 'Valor Orçado']],
      body: servicos.map((s, i) => [
        i + 1,
        s.nome_servico,
        s.descricao_servico_obra || '—',
        data(s.data_inicio),
        data(s.data_fim),
        labelStatus(s.status_servico),
        moeda(s.valor_orcado_os),
      ]),
      columnStyles: { 0: { cellWidth: 8 }, 3: { cellWidth: 20 }, 4: { cellWidth: 20 }, 5: { cellWidth: 28 }, 6: { cellWidth: 28, halign: 'right' } },
      foot: [['', '', '', '', '', 'Total Serviços', moeda(totalOrcado)]],
      footStyles: { fillColor: [243, 244, 246], textColor: 50, fontStyle: 'bold', fontSize: 8 },
      didParseCell(d) {
        if (d.section === 'head' && d.column.index === 6) d.cell.styles.halign = 'right';
        if (d.section === 'foot' && (d.column.index === 5 || d.column.index === 6)) d.cell.styles.halign = 'right';
      },
    });
    y = doc.lastAutoTable.finalY + 8;
  }

  // Materiais
  if (y > 220) { doc.addPage(); y = 20; }
  y = secao(doc, 'MATERIAIS', y, cor);
  const materiais = materiaisData?.materiais || [];
  if (materiais.length === 0) {
    doc.setFontSize(8); doc.setTextColor(...COR_CINZA);
    doc.text('Nenhum material cadastrado.', 14, y); y += 8;
  } else {
    autoTable(doc, {
      startY: y,
      margin: { left: 14, right: 14 },
      headStyles: { fillColor: cor, textColor: 255, fontSize: 8 },
      bodyStyles: { fontSize: 8 },
      alternateRowStyles: { fillColor: [249, 250, 251] },
      head: [[
        '#', 'Código', 'Descrição', 'Un.',
        { content: 'Qtd',         styles: { halign: 'right' } },
        { content: 'Preço Unit.', styles: { halign: 'right' } },
        { content: 'Total',       styles: { halign: 'right', cellPadding: { top: 2, right: 6, bottom: 2, left: 3 } } },
      ]],
      body: materiais.map((m, i) => [
        i + 1, m.codigo_sinapi || '—', m.descricao, m.unidade || '—',
        parseFloat(m.quantidade).toLocaleString('pt-BR'),
        moeda(m.preco_unitario), moeda(m.total),
      ]),
      columnStyles: { 0: { cellWidth: 8 }, 1: { cellWidth: 22 }, 3: { cellWidth: 12 }, 4: { cellWidth: 16, halign: 'right' }, 5: { cellWidth: 28, halign: 'right' }, 6: { cellWidth: 28, halign: 'right' } },
      foot: [['', '', '', '', '', 'Total Materiais', moeda(totalMat)]],
      footStyles: { fillColor: [243, 244, 246], textColor: 50, fontStyle: 'bold', fontSize: 8 },
      didParseCell(d) {
        if (d.section === 'foot' && (d.column.index === 5 || d.column.index === 6)) d.cell.styles.halign = 'right';
      },
    });
    y = doc.lastAutoTable.finalY + 8;
  }

  // Resumo Financeiro
  if (y > 230) { doc.addPage(); y = 20; }
  y = secao(doc, 'RESUMO FINANCEIRO', y, cor);
  autoTable(doc, {
    startY: y,
    margin: { left: 14, right: 14 },
    headStyles: { fillColor: cor, textColor: 255, fontSize: 8 },
    bodyStyles: { fontSize: 9 },
    head: [[
      'Descrição',
      { content: 'Valor', styles: { halign: 'right', cellPadding: { top: 2, right: 6, bottom: 2, left: 3 } } },
    ]],
    body: [
      ['Total do(s) Serviço(s)', moeda(totalOrcado)],
      ['Total de Materiais',     moeda(totalMat)],
      ['Valor Orçado',           moeda(valorOrcado)],
      ['Valor Fechado',          moeda(valorFechado)],
    ],
    columnStyles: { 0: { cellWidth: 130 }, 1: { halign: 'right' } },
    didParseCell(d) {
      if (d.row.index === 3 && d.column.index === 1) {
        d.cell.styles.textColor = cor;
        d.cell.styles.fontStyle = 'bold';
      }
    },
  });
  y = doc.lastAutoTable.finalY + 8;

  // Formas de Pagamento
  const pagamentos = pagamentoData?.pagamentos || [];
  if (pagamentos.length > 0) {
    if (y > 220) { doc.addPage(); y = 20; }
    y = secao(doc, 'FORMAS DE PAGAMENTO', y, cor);
    autoTable(doc, {
      startY: y,
      margin: { left: 14, right: 14 },
      headStyles: { fillColor: cor, textColor: 255, fontSize: 8 },
      bodyStyles: { fontSize: 8 },
      alternateRowStyles: { fillColor: [249, 250, 251] },
      head: [['Forma', 'Prazo / Vencimento', 'Parcelas', 'Valor Total']],
      body: pagamentos.map(p => [
        p.forma_pagamento,
        p.tem_dia_vencimento ? `Todo dia ${p.dia_vencimento}` : data(p.prazo_pagamento),
        p.parcelas ? `${p.parcelas}x` : '—',
        moeda(p.valor_total),
      ]),
      columnStyles: { 3: { halign: 'right' } },
    });
    y = doc.lastAutoTable.finalY + 8;
  }

  // Observações
  if (obra.observacoes) {
    if (y > 240) { doc.addPage(); y = 20; }
    y = secao(doc, 'OBSERVAÇÕES INTERNAS', y, cor);
    doc.setFontSize(8);
    doc.setTextColor(50, 50, 50);
    doc.setFont('helvetica', 'normal');
    const linhas = doc.splitTextToSize(obra.observacoes, 180);
    doc.text(linhas, 14, y);
  }

  rodape(doc, config);
  doc.save(nomeArquivo('relatorio-completo', obra));
}

// ─── PDF ORÇAMENTO ───────────────────────────────────────────────────────────

export function exportarOrcamento(obra, materiaisData, config = {}) {
  const cor = hexParaRgb(config?.cor);
  const doc = new jsPDF({ unit: 'mm', format: 'a4' });
  let y = cabecalho(doc, obra, config);

  const totalOrcado = obra.total_orcado || 0;
  const totalMat    = materiaisData?.total_materiais || 0;
  const valorOrcado = totalOrcado + totalMat;

  // Serviços — sem colunas Início/Fim
  y = secao(doc, 'SERVIÇOS', y, cor);
  const servicos = obra.servicos || [];
  if (servicos.length === 0) {
    doc.setFontSize(8); doc.setTextColor(...COR_CINZA);
    doc.text('Nenhum serviço cadastrado.', 14, y); y += 8;
  } else {
    autoTable(doc, {
      startY: y,
      margin: { left: 14, right: 14 },
      headStyles: { fillColor: cor, textColor: 255, fontSize: 8 },
      bodyStyles: { fontSize: 8 },
      alternateRowStyles: { fillColor: [249, 250, 251] },
      head: [['#', 'Serviço', 'Descrição', 'Valor Orçado']],
      body: servicos.map((s, i) => [
        i + 1, s.nome_servico, s.descricao_servico_obra || '—', moeda(s.valor_orcado_os),
      ]),
      columnStyles: { 0: { cellWidth: 8 }, 3: { cellWidth: 28, halign: 'right' } },
      foot: [['', '', 'Total Serviços', moeda(totalOrcado)]],
      didParseCell(d) {
        if (d.section === 'head' && d.column.index === 3) d.cell.styles.halign = 'right';
        if (d.section === 'foot' && (d.column.index === 2 || d.column.index === 3)) d.cell.styles.halign = 'right';
      },
      footStyles: { fillColor: [243, 244, 246], textColor: 50, fontStyle: 'bold', fontSize: 8 },
    });
    y = doc.lastAutoTable.finalY + 8;
  }

  // Materiais
  if (y > 220) { doc.addPage(); y = 20; }
  y = secao(doc, 'MATERIAIS', y, cor);
  const materiais = materiaisData?.materiais || [];
  if (materiais.length === 0) {
    doc.setFontSize(8); doc.setTextColor(...COR_CINZA);
    doc.text('Nenhum material cadastrado.', 14, y); y += 8;
  } else {
    autoTable(doc, {
      startY: y,
      margin: { left: 14, right: 14 },
      headStyles: { fillColor: cor, textColor: 255, fontSize: 8 },
      bodyStyles: { fontSize: 8 },
      alternateRowStyles: { fillColor: [249, 250, 251] },
      head: [[
        '#', 'Código', 'Descrição', 'Un.',
        { content: 'Qtd',         styles: { halign: 'right' } },
        { content: 'Preço Unit.', styles: { halign: 'right' } },
        { content: 'Total',       styles: { halign: 'right', cellPadding: { top: 2, right: 6, bottom: 2, left: 3 } } },
      ]],
      body: materiais.map((m, i) => [
        i + 1, m.codigo_sinapi || '—', m.descricao, m.unidade || '—',
        parseFloat(m.quantidade).toLocaleString('pt-BR'),
        moeda(m.preco_unitario), moeda(m.total),
      ]),
      columnStyles: { 0: { cellWidth: 8 }, 1: { cellWidth: 22 }, 3: { cellWidth: 12 }, 4: { cellWidth: 16, halign: 'right' }, 5: { cellWidth: 28, halign: 'right' }, 6: { cellWidth: 28, halign: 'right' } },
      foot: [['', '', '', '', '', 'Total Materiais', moeda(totalMat)]],
      footStyles: { fillColor: [243, 244, 246], textColor: 50, fontStyle: 'bold', fontSize: 8 },
      didParseCell(d) {
        if (d.section === 'foot' && (d.column.index === 5 || d.column.index === 6)) d.cell.styles.halign = 'right';
      },
    });
    y = doc.lastAutoTable.finalY + 8;
  }

  // Resumo Financeiro — sem Valor Fechado
  if (y > 230) { doc.addPage(); y = 20; }
  y = secao(doc, 'RESUMO FINANCEIRO', y, cor);
  autoTable(doc, {
    startY: y,
    margin: { left: 14, right: 14 },
    headStyles: { fillColor: cor, textColor: 255, fontSize: 8 },
    bodyStyles: { fontSize: 9 },
    head: [[
      'Descrição',
      { content: 'Valor', styles: { halign: 'right', cellPadding: { top: 2, right: 6, bottom: 2, left: 3 } } },
    ]],
    body: [
      ['Total do(s) Serviço(s)', moeda(totalOrcado)],
      ['Total de Materiais',     moeda(totalMat)],
      ['Valor Orçado Total',     moeda(valorOrcado)],
    ],
    columnStyles: { 0: { cellWidth: 154 }, 1: { halign: 'right' } },
    didParseCell(d) {
      if (d.row.index === 2 && d.column.index === 1) {
        d.cell.styles.textColor = cor;
        d.cell.styles.fontStyle = 'bold';
      }
    },
  });
  y = doc.lastAutoTable.finalY + 8;

  // Observações
  if (obra.observacoes) {
    if (y > 240) { doc.addPage(); y = 20; }
    y = secao(doc, 'OBSERVAÇÕES', y, cor);
    doc.setFontSize(8);
    doc.setTextColor(50, 50, 50);
    doc.setFont('helvetica', 'normal');
    const linhas = doc.splitTextToSize(obra.observacoes, 180);
    doc.text(linhas, 14, y);
  }

  rodape(doc, config);
  doc.save(nomeArquivo('orcamento', obra));
}

// ─── RELATÓRIO DE SERVIÇOS E MATERIAIS (sem valores individuais) ─────────────

export function exportarServicosMateriais(obra, materiaisData, config = {}) {
  const cor = hexParaRgb(config?.cor);
  const doc = new jsPDF({ unit: 'mm', format: 'a4' });
  let y = cabecalho(doc, obra, config);

  const totalOrcado = obra.total_orcado || 0;
  const totalMat    = materiaisData?.total_materiais || 0;

  y = secao(doc, 'SERVIÇOS', y, cor);
  const servicos = obra.servicos || [];
  if (servicos.length === 0) {
    doc.setFontSize(8); doc.setTextColor(...COR_CINZA);
    doc.text('Nenhum serviço cadastrado.', 14, y); y += 8;
  } else {
    autoTable(doc, {
      startY: y,
      margin: { left: 14, right: 14 },
      headStyles: { fillColor: cor, textColor: 255, fontSize: 8 },
      bodyStyles: { fontSize: 8 },
      alternateRowStyles: { fillColor: [249, 250, 251] },
      head: [['#', 'Serviço', 'Descrição', 'Início', 'Fim', 'Status']],
      body: servicos.map((s, i) => [
        i + 1,
        s.nome_servico,
        s.descricao_servico_obra || '—',
        data(s.data_inicio),
        data(s.data_fim),
        labelStatus(s.status_servico),
      ]),
      columnStyles: { 0: { cellWidth: 8 }, 3: { cellWidth: 22 }, 4: { cellWidth: 22 }, 5: { cellWidth: 28 } },
    });
    y = doc.lastAutoTable.finalY + 8;
  }

  // Total dos Serviços (logo após a lista de serviços)
  if (y > 250) { doc.addPage(); y = 20; }
  autoTable(doc, {
    startY: y,
    margin: { left: 14, right: 14 },
    bodyStyles: { fontSize: 9, fontStyle: 'bold', textColor: [50, 50, 50] },
    body: [['Valor Total dos Serviços', moeda(totalOrcado)]],
    columnStyles: { 0: { cellWidth: 150 }, 1: { halign: 'right' } },
  });
  y = doc.lastAutoTable.finalY + 8;

  // Materiais — sem preço unitário nem total por linha
  if (y > 220) { doc.addPage(); y = 20; }
  y = secao(doc, 'MATERIAIS', y, cor);
  const materiais = materiaisData?.materiais || [];
  if (materiais.length === 0) {
    doc.setFontSize(8); doc.setTextColor(...COR_CINZA);
    doc.text('Nenhum material cadastrado.', 14, y); y += 8;
  } else {
    autoTable(doc, {
      startY: y,
      margin: { left: 14, right: 14 },
      headStyles: { fillColor: cor, textColor: 255, fontSize: 8 },
      bodyStyles: { fontSize: 8 },
      alternateRowStyles: { fillColor: [249, 250, 251] },
      head: [['#', 'Descrição', 'Un.', { content: 'Qtd', styles: { halign: 'right' } }]],
      body: materiais.map((m, i) => [
        i + 1, m.descricao, m.unidade || '—',
        parseFloat(m.quantidade).toLocaleString('pt-BR'),
      ]),
      columnStyles: { 0: { cellWidth: 8 }, 2: { cellWidth: 16 }, 3: { cellWidth: 20, halign: 'right' } },
    });
    y = doc.lastAutoTable.finalY + 8;
  }

  // Total dos Materiais (logo após a lista de materiais)
  if (y > 250) { doc.addPage(); y = 20; }
  autoTable(doc, {
    startY: y,
    margin: { left: 14, right: 14 },
    bodyStyles: { fontSize: 9, fontStyle: 'bold', textColor: [50, 50, 50] },
    body: [['Valor Total dos Materiais', moeda(totalMat)]],
    columnStyles: { 0: { cellWidth: 150 }, 1: { halign: 'right' } },
  });
  y = doc.lastAutoTable.finalY + 8;

  // Soma final (Serviços + Materiais)
  if (y > 250) { doc.addPage(); y = 20; }
  autoTable(doc, {
    startY: y,
    margin: { left: 14, right: 14 },
    bodyStyles: { fontSize: 9, fontStyle: 'bold' },
    body: [['Valor Total (Serviços + Materiais)', moeda(totalOrcado + totalMat)]],
    columnStyles: { 0: { cellWidth: 150 }, 1: { halign: 'right', textColor: cor } },
  });

  rodape(doc, config);
  doc.save(nomeArquivo('servicos-materiais', obra));
}

// ─── RELATÓRIO FINANCEIRO ─────────────────────────────────────────────────────

export function exportarRelatorioFinanceiro(obra, pagamentoData, config = {}) {
  const cor = hexParaRgb(config?.cor);
  const doc = new jsPDF({ unit: 'mm', format: 'a4' });
  let y = cabecalho(doc, obra, config);

  const valorFechado   = parseFloat(obra.valor_fechado_obra || 0);
  const totalRecebido  = parseFloat(pagamentoData?.total_recebido || 0);
  const saldoPendente  = Math.max(valorFechado - totalRecebido, 0);

  // Resumo
  y = secao(doc, 'RESUMO FINANCEIRO', y, cor);
  autoTable(doc, {
    startY: y,
    margin: { left: 14, right: 14 },
    headStyles: { fillColor: cor, textColor: 255, fontSize: 8 },
    bodyStyles: { fontSize: 10 },
    head: [['Descrição', 'Valor']],
    body: [
      ['Valor Fechado (contrato)',   moeda(valorFechado)],
      ['Total Já Recebido',          moeda(totalRecebido)],
      ['Saldo Pendente',             moeda(saldoPendente)],
    ],
    columnStyles: { 0: { cellWidth: 130 }, 1: { halign: 'right' } },
    didParseCell(d) {
      if (d.section !== 'body') return;
      if (d.column.index !== 1) return;
      if (d.row.index === 1) d.cell.styles.textColor = COR_VERDE;
      if (d.row.index === 2 && saldoPendente > 0) d.cell.styles.textColor = COR_VERMELHO;
    },
  });
  y = doc.lastAutoTable.finalY + 8;

  // Formas de Pagamento
  const pagamentos = pagamentoData?.pagamentos || [];
  if (pagamentos.length > 0) {
    y = secao(doc, 'FORMAS DE PAGAMENTO', y, cor);

    for (const p of pagamentos) {
      const recebidoNaForma = (pagamentoData?.recebimentos || [])
        .filter(r => r.forma_pagamento === p.forma_pagamento)
        .reduce((acc, r) => acc + parseFloat(r.valor_recebido || 0), 0);
      const pendente = Math.max(parseFloat(p.valor_total || 0) - recebidoNaForma, 0);

      doc.setFontSize(9); doc.setFont('helvetica', 'bold'); doc.setTextColor(50, 50, 50);
      doc.text(p.forma_pagamento, 14, y);
      doc.setFont('helvetica', 'normal'); doc.setFontSize(8); doc.setTextColor(...COR_CINZA);
      const prazoStr = p.tem_dia_vencimento ? `Vencimento todo dia ${p.dia_vencimento}` : `Prazo: ${data(p.prazo_pagamento)}`;
      doc.text(`${prazoStr}   |   ${p.parcelas ? p.parcelas + 'x' : 'À vista'}   |   Total: ${moeda(p.valor_total)}   |   Recebido: ${moeda(recebidoNaForma)}   |   Pendente: ${moeda(pendente)}`, 14, y + 5);
      y += 10;

      const recebsDaForma = (pagamentoData?.recebimentos || []).filter(r => r.forma_pagamento === p.forma_pagamento);
      if (recebsDaForma.length > 0) {
        autoTable(doc, {
          startY: y,
          margin: { left: 18, right: 14 },
          headStyles: { fillColor: [229, 231, 235], textColor: 50, fontSize: 7.5 },
          bodyStyles: { fontSize: 8 },
          head: [['Data do Pagamento', 'Valor Recebido', 'Observação']],
          body: recebsDaForma.map(r => [
            data(r.data_recebimento), moeda(r.valor_recebido), r.observacao || '—',
          ]),
          columnStyles: { 0: { cellWidth: 28 }, 1: { cellWidth: 23 }, 2: { cellWidth: 118 } },
          didParseCell(d) {
            if (d.column.index === 1) d.cell.styles.halign = 'right';
          },
        });
        y = doc.lastAutoTable.finalY + 6;
      } else {
        doc.setFontSize(8); doc.setTextColor(...COR_CINZA);
        doc.setFont('helvetica', 'italic');
        doc.text('Nenhum recebimento registrado.', 18, y);
        y += 6;
      }
      y += 2;
    }
  } else {
    doc.setFontSize(8); doc.setTextColor(...COR_CINZA);
    doc.text('Nenhuma forma de pagamento cadastrada.', 14, y);
  }

  rodape(doc, config);
  doc.save(nomeArquivo('relatorio-financeiro', obra));
}

// ─── PDF MATERIAIS (colunas dinâmicas) ────────────────────────────────────────

export function exportarMateriais(obra, materiaisData, colunas, config = {}) {
  const cor = hexParaRgb(config?.cor);
  const materiais = materiaisData?.materiais || [];

  const nOpcoes = Object.values(colunas).filter(Boolean).length;
  const totalCols = 3 + nOpcoes;
  const land = totalCols >= 8;
  const abrev = land;

  const doc = new jsPDF({ unit: 'mm', format: 'a4', orientation: land ? 'landscape' : 'portrait' });
  let y = cabecalho(doc, obra, config);

  y = secao(doc, 'MATERIAIS', y, cor);

  if (materiais.length === 0) {
    doc.setFontSize(8); doc.setTextColor(...COR_CINZA);
    doc.text('Nenhum material cadastrado.', 14, y);
    rodape(doc, config);
    doc.save(nomeArquivo('materiais', obra));
    return;
  }

  const defs = [
    { key: 'num',       headFull: '#',         headAbrev: '#',         wP: 8,    wL: 8,    align: 'left',  val: (m, i) => i + 1 },
    { key: 'codigo',    headFull: 'Código',     headAbrev: 'Código',    wP: 20,   wL: 18,   align: 'left',  val: m => m.codigo_sinapi || '—' },
    { key: 'descricao', headFull: 'Descrição',  headAbrev: 'Desc.',     wP: null, wL: null, align: 'left',  val: m => m.descricao },
  ];
  if (colunas.unidade)        defs.push({ key: 'unidade',        headFull: 'Un.',          headAbrev: 'Un.',        wP: 12, wL: 10, align: 'left',  val: m => m.unidade || '—' });
  if (colunas.quantidade)     defs.push({ key: 'quantidade',     headFull: 'Qtd.',         headAbrev: 'Qtd.',       wP: 16, wL: 14, align: 'right', val: m => parseFloat(m.quantidade).toLocaleString('pt-BR', { maximumFractionDigits: 4 }) });
  if (colunas.preco_unitario) defs.push({ key: 'preco_unitario', headFull: 'Preço Unit.',  headAbrev: 'Preço Un.',  wP: 24, wL: 22, align: 'right', val: m => moeda(m.preco_unitario) });
  if (colunas.total)          defs.push({ key: 'total',          headFull: 'Total',        headAbrev: 'Total',      wP: 24, wL: 22, align: 'right', val: m => moeda(m.total) });
  if (colunas.fornecedor)     defs.push({ key: 'fornecedor',     headFull: 'Fornecedor',   headAbrev: 'Forn.',      wP: 30, wL: 26, align: 'left',  val: m => m.nome_fornecedor || '—' });
  if (colunas.valor_compra)   defs.push({ key: 'valor_compra',   headFull: 'Vlr. Compra',  headAbrev: 'Vlr. Comp.', wP: 24, wL: 22, align: 'right', val: m => m.valor_compra ? moeda(m.valor_compra) : '—' });
  if (colunas.data_compra)    defs.push({ key: 'data_compra',    headFull: 'Dt. Compra',   headAbrev: 'Dt. Comp.',  wP: 20, wL: 18, align: 'left',  val: m => data(m.data_compra) });
  if (colunas.data_entrega)   defs.push({ key: 'data_entrega',   headFull: 'Dt. Entrega',  headAbrev: 'Dt. Entr.',  wP: 20, wL: 18, align: 'left',  val: m => data(m.data_entrega) });

  const head = [defs.map(col => {
    const h = abrev ? col.headAbrev : col.headFull;
    return col.align === 'right' ? { content: h, styles: { halign: 'right' } } : h;
  })];

  const body = materiais.map((m, i) => defs.map(col => col.val(m, i)));

  const columnStyles = {};
  defs.forEach((col, i) => {
    const w = land ? col.wL : col.wP;
    const s = {};
    if (w)                     s.cellWidth = w;
    if (col.align === 'right') s.halign = 'right';
    if (Object.keys(s).length) columnStyles[i] = s;
  });

  const totalMateriais = materiaisData?.total_materiais || 0;
  const totalCompra = colunas.valor_compra
    ? materiais.reduce((acc, m) => acc + parseFloat(m.valor_compra || 0), 0)
    : 0;

  let foot;
  if (colunas.total || colunas.valor_compra) {
    const totalIdx   = defs.findIndex(c => c.key === 'total');
    const compraIdx  = defs.findIndex(c => c.key === 'valor_compra');
    const firstValIdx = [totalIdx, compraIdx].filter(i => i >= 0).sort((a, b) => a - b)[0];
    const row = [{ content: 'Total de Materiais', colSpan: firstValIdx, styles: { halign: 'left', fontStyle: 'bold' } }];
    defs.slice(firstValIdx).forEach(col => {
      if (col.key === 'total')             row.push({ content: moeda(totalMateriais), styles: { halign: 'right' } });
      else if (col.key === 'valor_compra') row.push({ content: moeda(totalCompra), styles: { halign: 'right' } });
      else row.push('');
    });
    foot = [row];
  }

  autoTable(doc, {
    startY: y,
    margin: { left: 14, right: 14 },
    headStyles: { fillColor: cor, textColor: 255, fontSize: 8 },
    bodyStyles: { fontSize: 8 },
    alternateRowStyles: { fillColor: [249, 250, 251] },
    head, body, columnStyles,
    ...(foot ? { foot, footStyles: { fillColor: [243, 244, 246], textColor: 50, fontStyle: 'bold', fontSize: 8 } } : {}),
  });

  rodape(doc, config);
  doc.save(nomeArquivo('materiais', obra));
}
